import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Check, X, RotateCcw } from "lucide-react";

/**
 * Reusable image-crop modal.
 *
 * Usage:
 *   const [pendingFile, setPendingFile] = useState(null);
 *   <ImageCropModal
 *     file={pendingFile}
 *     aspect={1}
 *     onCancel={() => setPendingFile(null)}
 *     onConfirm={(blob) => upload(blob)}
 *   />
 *
 * Props:
 *   file     — the File object the user picked
 *   aspect   — desired crop aspect ratio (default 1 = square)
 *   onCancel — close without upload
 *   onConfirm(blob) — receives a cropped JPEG Blob ready to upload
 */
const ImageCropModal = ({ file, aspect = 1, onCancel, onConfirm }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  // Read the picked file EVERY time it changes (not just once).
  // Without this, opening the cropper for a SECOND image would still
  // show the first image because internal state was never reset.
  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
    // Reset any previous crop/zoom/rotation so the new image starts fresh
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
  }, [file]);

  const onCropComplete = useCallback((_area, areaPx) => {
    setCroppedAreaPixels(areaPx);
  }, []);

  const cropToBlob = async () => {
    if (!imageSrc || !croppedAreaPixels) return null;
    const img = new Image();
    img.src = imageSrc;
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    // Account for rotation
    const rad = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rad));
    const cos = Math.abs(Math.cos(rad));
    const bBoxWidth = img.width * cos + img.height * sin;
    const bBoxHeight = img.width * sin + img.height * cos;
    // First, rotate the image onto a tmp canvas
    const tmp = document.createElement("canvas");
    tmp.width = bBoxWidth;
    tmp.height = bBoxHeight;
    const tctx = tmp.getContext("2d");
    tctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    tctx.rotate(rad);
    tctx.drawImage(img, -img.width / 2, -img.height / 2);
    // Then crop the requested area from the rotated tmp
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
    ctx.drawImage(
      tmp,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );
    return await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
    });
  };

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const blob = await cropToBlob();
      if (blob) {
        // Wrap blob into a File so backend filename extension stays sensible
        const f = new File([blob], (file?.name || "cropped").replace(/\.[^.]+$/, "") + ".jpg", {
          type: "image/jpeg",
        });
        await onConfirm(f);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!file) return null;

  return (
    <div
      data-testid="image-crop-modal"
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-3"
      dir="rtl"
    >
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white px-4 py-3 flex items-center justify-between">
          <h3 className="font-bold">قص وتعديل الصورة</h3>
          <button
            onClick={onCancel}
            data-testid="crop-cancel"
            className="p-1.5 hover:bg-white/20 rounded-full"
            disabled={busy}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative w-full bg-black" style={{ height: 360 }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs text-[#5C6E64] flex justify-between mb-1">
              <span>التكبير</span>
              <span dir="ltr">{zoom.toFixed(2)}×</span>
            </label>
            <input
              type="range"
              min={1}
              max={5}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="crop-zoom"
              className="w-full accent-[#3B82F6]"
            />
          </div>
          <div>
            <label className="text-xs text-[#5C6E64] flex justify-between mb-1">
              <span>الدوران</span>
              <span dir="ltr">{rotation}°</span>
            </label>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotation}
              onChange={(e) => setRotation(Number(e.target.value))}
              data-testid="crop-rotation"
              className="w-full accent-[#3B82F6]"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => { setZoom(1); setRotation(0); setCrop({ x: 0, y: 0 }); }}
              data-testid="crop-reset"
              className="flex-1 flex items-center justify-center gap-1 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] py-2 rounded-full text-sm"
              disabled={busy}
            >
              <RotateCcw className="w-4 h-4" />إعادة
            </button>
            <button
              onClick={handleConfirm}
              data-testid="crop-confirm"
              disabled={busy || !croppedAreaPixels}
              className="flex-1 flex items-center justify-center gap-1 bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-2 rounded-full text-sm"
            >
              <Check className="w-4 h-4" />
              {busy ? "جاري الحفظ..." : "تأكيد"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
